import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import Toggle from "react-toggle"
import GlobalHelper from "../../../common/GlobalHelper.jsx"
import ConnectIntegrationButton from "../../../common/button/connectintegrationbutton.component.jsx"
import { Intl } from "../../../state/helpers/IntlProps.js"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import IntegrationConfigurationValue from "../../../state/integration/IntegrationConfigurationValue.js"
import {
    FeatureApprovedQuoteAutoExport,
    FeatureContactExport,
    FeatureContactImport,
    FeatureCreateCreditMemoPaymentImport,
    FeatureExportOrderLineDetailsToPurchaseOrder,
    FeatureExportOrganizationsOnInvoices,
    FeatureExternalOrderCronImport,
    FeatureExternalPricingCalculation,
    FeatureInventoryCronImport,
    FeatureInventorySync,
    FeatureInvoiceCronImport,
    FeaturePaymentSync,
    FeatureProductExport,
    FeatureProductImportIgnoreSkuMatching,
    FeatureProductSync,
    FeaturePurchaseOrderTracking,
    FeatureSentInvoiceAutoExport,
    FeatureShortDescriptionProductImport,
} from "../../../state/integration/IntegrationFeatureKeys"
import { integrationKeyLightspeed } from "../../IntegrationConstants.jsx"
import ConfigurationValueComponent from "../ConfigurationValueComponent"
import FeatureToggleComponent from "../FeatureToggleComponent"

const messages = defineMessages({
    "custom_on_account_payment_type_default_value": {
        id: "integration.form.edit.lightspeed.custom_on_account_payment_type_default_value",
        defaultMessage: "Standard - Non-Golf",
    },
})

enum Setting {
    REGISTER_ID = "registerId",
    EMPLOYEE_ID = "employeeId",
    BANK_TRANSFER_PAYMENT_TYPE_ID = "bankTransferPaymentTypeId",
    CASH_PAYMENT_TYPE_ID = "cashPaymentTypeId",
    CREDIT_CARD_PAYMENT_TYPE_ID = "creditCardPaymentTypeId",
    CHECK_PAYMENT_TYPE_ID = "checkPaymentTypeId",
    PRICE_LIST_CREDIT_MEMO_PAYMENT_TYPE_ID = "priceListCreditMemoPaymentTypeId",
    TRANSFER_PAYMENT_TYPE_ID = "transferPaymentTypeId",
    ON_ACCOUNT_SALE_PAYMENT_TYPE_ID = "onAccountSalePaymentTypeId",
    ON_ACCOUNT_PAYMENT_TYPE_ID = "onAccountPaymentTypeId"
}

type OwnProps = {
    integrationConfigurationId: string,
    configuration: object,
    configurationValues: IntegrationConfigurationValue[],
    enabledFeatures: object,
    onFeatureToggled: (featureKey: string) => void,
    onConfigurationChange: (configuration: object) => void,
    toggleSaveButtonStatus: (enabled: boolean) => void,
    intl: Intl,
}

const LsRetailFormComponent = (props: OwnProps) => {

    const [shouldUseCustomOnAccountPaymentTypes, setShouldUseCustomOnAccountPaymentTypes] = useState<boolean>(
        props.configurationValues.filter((c) => {
            return [Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID, Setting.ON_ACCOUNT_PAYMENT_TYPE_ID].includes(c.getKey() as Setting)
                && props.configuration[c.getKey()]
        }).length > 0
    )

    useEffect(() => {
        props.toggleSaveButtonStatus(isSaveEnabled(props.configuration))
    }, [props.configuration])

    const renderConfigurationValues = () => {
        return <>
            {props.configurationValues.map((c, i) => {
                if (shouldRenderConfigurationAttribute(c.getKey()) &&
                    ![Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID, Setting.ON_ACCOUNT_PAYMENT_TYPE_ID].includes(c.getKey() as Setting)) {

                    return <ConfigurationValueComponent
                        key={i}
                        value={props.configuration[c.getKey()]}
                        configurationValue={c}
                        onChange={(newValue) => {
                            const conf = props.configuration
                            conf[c.getKey()] = newValue
                            props.toggleSaveButtonStatus(isSaveEnabled(conf))
                            props.onConfigurationChange(conf)
                        }}
                        renderConfigurationValueLabel={renderConfigurationValueLabel}
                    />
                }
            })}

            <FeatureToggleComponent
                key={"use-custom-on-account-payment-types"}
                featureKey={"use-custom-on-account-payment-types"}
                checked={shouldUseCustomOnAccountPaymentTypes}
                onFeatureToggled={() => {
                    //if it was previously true
                    if (shouldUseCustomOnAccountPaymentTypes) {
                        const conf = props.configuration
                        conf[Setting.ON_ACCOUNT_PAYMENT_TYPE_ID] = ""
                        conf[Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID] = ""
                        props.toggleSaveButtonStatus(isSaveEnabled(conf))
                        props.onConfigurationChange(conf)
                    }
                    setShouldUseCustomOnAccountPaymentTypes(!shouldUseCustomOnAccountPaymentTypes)
                }}
                renderFeatureLabel={renderFeatureLabel}
            />

            {shouldUseCustomOnAccountPaymentTypes &&
                props.configurationValues.filter((c) => {
                    return [Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID, Setting.ON_ACCOUNT_PAYMENT_TYPE_ID].includes(c.getKey() as Setting)
                }).map((c, i) => {
                    c.addValue("", props.intl.formatMessage(messages["custom_on_account_payment_type_default_value"]))

                    return <ConfigurationValueComponent
                        key={i}
                        value={props.configuration[c.getKey()]}
                        configurationValue={c}
                        onChange={(newValue) => {
                            const conf = props.configuration
                            conf[c.getKey()] = newValue
                            props.toggleSaveButtonStatus(isSaveEnabled(conf))
                            props.onConfigurationChange(conf)
                        }}
                        renderConfigurationValueLabel={renderConfigurationValueLabel}
                        hideDefaultOption={true}
                    />
                })
            }
        </>
    }

    if (!props.integrationConfigurationId) {
        const url = AppContext.getOptions().apiUrl + "/openid/connect/ls-retail/" +
            AppContext.getUserLoggedIn().id + "?r=/integrations/listing?open=ls-retail"
        return <div className="col-12 ui-module">
            <div className="form-group row">
                <label className="col-3 col-form-label text-align-right">
                    <FormattedMessage
                        id="integration.form.edit.lightspeed.connect"
                        defaultMessage="Connect"
                    />
                </label>
                <div className="col-9">
                    <ConnectIntegrationButton integrationKey={INTEGRATION_KEY.LIGHTSPEED_RETAIL} url={url} />
                </div>
            </div>
        </div>
    }

    return <div className="col-12 ui-module">
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="realmId">
                <FormattedMessage
                    id="integration.form.edit.lightspeed.account_id"
                    defaultMessage="Account ID"
                />
            </label>
            <div className="col-9">
                <input
                    name="accountId"
                    value={props.configuration["accountId"]}
                    disabled={true}
                    id="accountId"
                    type="text"
                    className="form-control"
                />
            </div>
        </div>
        {AppContext.getUserLoggedIn().attributes.superUser &&
            [
                <div className="form-group row" key={"isUpdateFeedDisabled"}>
                    <label className="col-3 col-form-label text-align-right" htmlFor={"isUpdateFeedDisabled"}>
                        <FormattedMessage
                            id="integration.form.edit.lightspeed.disable_update_feed"
                            defaultMessage="Disable Update Feed (staff only)"
                        />
                    </label>
                    <div className="col-9 d-flex align-items-center">
                        <Toggle
                            id={"isUpdateFeedDisabled"}
                            defaultChecked={props.configuration["isUpdateFeedDisabled"]}
                            icons={false}
                            onChange={() => {
                                props.onConfigurationChange({
                                    ...props.configuration,
                                    ...{ isUpdateFeedDisabled: !props.configuration["isUpdateFeedDisabled"] },
                                })
                            }}
                        />
                    </div>
                </div>,
                <div className="form-group row" key={"maximumNumberOfItemsPerPage"}>
                    <label className="col-3 col-form-label text-align-right" htmlFor={"maximumNumberOfItemsPerPage"}>
                        <FormattedMessage
                            id="integration.form.edit.lightspeed.max_items_per_page_api"
                            defaultMessage="Max number of processed of items per page (API)"
                        />
                    </label>
                    <div className="col-9 d-flex align-items-center">
                        <input
                            name="maximumNumberOfItemsPerPage"
                            value={props.configuration["maximumNumberOfItemsPerPage"]}
                            id="maximumNumberOfItemsPerPage"
                            type="number"
                            className="form-control"
                            onChange={(e) => {
                                props.onConfigurationChange({
                                    ...props.configuration,
                                    ...{ maximumNumberOfItemsPerPage: parseInt(e.target.value) },
                                })
                            }}
                        />
                    </div>
                </div>
            ]
        }

        {renderFeatureToggles(props)}
        {renderConfigurationValues()}

    </div>
}

const renderFeatureToggles = (props: OwnProps) => {
    return Object.keys(props.enabledFeatures).map((featureKey, i) => {
        if (featureKey === "payment-sync") { // invoice and payment are sync together now
            return
        }
        if (featureKey == FeatureContactImport && GlobalHelper.hasAnotherIntegrationWithFeatureEnabled(
            integrationKeyLightspeed,
            FeatureContactImport)
        ) {
            return
        }

        return <FeatureToggleComponent
            key={i}
            featureKey={featureKey}
            checked={props.enabledFeatures[featureKey]}
            onFeatureToggled={() => {
                props.onFeatureToggled(featureKey)
            }}
            renderFeatureLabel={renderFeatureLabel}
        />
    })
}

const renderFeatureLabel = (key: string) => {
    switch (key) {
    case FeatureApprovedQuoteAutoExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.approved_quote_auto_export"
            defaultMessage="Auto export approved quote"
        />
    case FeatureContactExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.contact_export"
            defaultMessage="Contact export"
        />
    case FeatureContactImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.contact_import"
            defaultMessage="Contact import"
        />

    case FeatureInventoryCronImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.inventory_sync"
            defaultMessage="Inventory sync"
        />
    case FeatureInventorySync:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.show_inventory"
            defaultMessage="Show inventory"
        />
    case FeatureProductSync:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.product_sync"
            defaultMessage="Product sync"
        />
    case FeatureSentInvoiceAutoExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.sent_invoice_auto_export_sync"
            defaultMessage="Auto export of sent invoice"
        />
    case FeatureProductExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.product_export"
            defaultMessage="Export products"
        />
    case FeatureInvoiceCronImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.invoice_sync"
            defaultMessage="Auto import sales paid with a credit account"
        />
    case FeaturePaymentSync:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.payment_sync"
            defaultMessage="Auto import deposit and payment made on credit account"
        />
    case FeatureExternalPricingCalculation:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.external_pricing_calculation"
            defaultMessage="Apply price rules"
        />
    case FeatureExportOrganizationsOnInvoices:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.export_organizations_on_invoices"
            defaultMessage="Contact on exported invoices should be the organization when possible"
        />
    case FeatureProductImportIgnoreSkuMatching:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.product_import_ignore_sku_matching"
            defaultMessage="SKUs are not unique"
        />
    case FeatureShortDescriptionProductImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.product_import_short_description"
            defaultMessage="Import product short description"
        />
    case FeatureExternalOrderCronImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.external_order_sync"
            defaultMessage="Sales Receipt Sync"
        />
    case FeatureCreateCreditMemoPaymentImport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.payment_import_create_credit_memo"
            defaultMessage="Create credit memos when payments are imported instead of applying them automatically"
        />
    case FeatureExportOrderLineDetailsToPurchaseOrder:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.export_order_line_details_to_purchase_order_description"
            defaultMessage="Export order lines notes to PO"
        />
    case FeaturePurchaseOrderTracking:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.purchase_order_tracking_description"
            defaultMessage="Create and track PO from orders"
        />
    case "use-custom-on-account-payment-types":
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.use_custom_on_account_description"
            defaultMessage="Use custom payment types for On Account transactions (LS Golf)"
        />
    default:
        return key
    }
}

const renderConfigurationValueLabel = (key: string) => {
    switch (key) {
    case "shopId":
        return <FormattedMessage
            id="integration.form.edit.lightspeed.shop_id"
            defaultMessage="Shop"
        />
    case Setting.REGISTER_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.register_id"
            defaultMessage="Register"
        />
    case Setting.EMPLOYEE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.employee_id"
            defaultMessage="Employee"
        />
    case Setting.BANK_TRANSFER_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.bank_transfer_payment_type_id"
            defaultMessage="Payment type for bank transfer"
        />
    case Setting.CASH_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.cash_payment_type_id"
            defaultMessage="Payment type for cash"
        />
    case Setting.CREDIT_CARD_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.credit_card_payment_type_id"
            defaultMessage="Payment type for credit card"
        />
    case Setting.CHECK_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.check_payment_type_id"
            defaultMessage="Payment type for cheque"
        />
    case Setting.PRICE_LIST_CREDIT_MEMO_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.price_list_credit_memo_payment_type_id"
            defaultMessage="Event Registration Deposit payment type"
        />
    case Setting.TRANSFER_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.transfer_payment_type_id"
            defaultMessage="Credit transfers between customer accounts"
        />
    case Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.on_account_sale_payment_type_id"
            defaultMessage="Payment type to record on account sales"
        />
    case Setting.ON_ACCOUNT_PAYMENT_TYPE_ID:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.on_account_payment_type_id"
            defaultMessage="Payment type to record on account payments"
        />
    default:
        return key
    }
}

const shouldRenderConfigurationAttribute = (configurationValueKey: string) => {
    if (configurationValueKey === "priceListCreditMemoPaymentTypeId" && !GlobalHelper.hasGolfIntegration()) {
        return false
    }
    return true
}

const isSaveEnabled = (configuration: object) => {
    if (!GlobalHelper.hasGolfIntegration()) {
        delete configuration["priceListCreditMemoPaymentTypeId"]
    }
    return !(Object.keys(configuration).filter(
        key => {
            return configuration[key] === "" &&
                //these 2 settings can stay empty
                ![Setting.ON_ACCOUNT_SALE_PAYMENT_TYPE_ID, Setting.ON_ACCOUNT_PAYMENT_TYPE_ID].includes(key as Setting)
        }
    ).length > 0)
}

export default injectIntl(LsRetailFormComponent)


// WEBPACK FOOTER //
// ./src/integration/form/ls-retail/LSRetailFormComponent.tsx